import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ChevronsRight, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { toProperCase, toSentenceCase } from '../utils/textFormat';

import EntryFormFields from './entry/EntryFormFields';
import EntryPicturesPanel from './entry/EntryPicturesPanel';
import EntryDocChecklist from './entry/EntryDocChecklist';
import {
  mapClaimToForm, PROPER_FIELDS, SENTENCE_FIELDS, validateBeforeSave,
} from './entry/entryHelpers';

import { API_BASE as API } from '../config';

/**
 * Right-side edit drawer for an existing claim. Owns formData/originalData state,
 * save+delete handlers, and composes the form fields, pictures panel, and doc
 * checklist sub-components.
 */
const EditEntryPanel = ({ claim, open, onClose, companies, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [pictures, setPictures] = useState([]);
  const [picIndex, setPicIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  useEffect(() => {
    if (!claim) return;
    const initial = mapClaimToForm(claim);
    setFormData(initial);
    setOriginalData(initial);
    try {
      const pics = claim.pictures
        ? (typeof claim.pictures === 'string' ? JSON.parse(claim.pictures) : claim.pictures)
        : [];
      setPictures(Array.isArray(pics) ? pics : []);
    } catch {
      setPictures([]);
    }
  }, [claim]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      let v = value;
      if (typeof v === 'string') {
        if (PROPER_FIELDS.includes(field)) v = toProperCase(v);
        else if (SENTENCE_FIELDS.includes(field)) v = toSentenceCase(v);
      }
      const updated = { ...prev, [field]: v };
      if (field === 'police_involved' && value === 'No') {
        updated.citation = '';
        updated.police_report_no = '';
        updated.police_report_not_ready = false;
      }
      if (field === 'police_report_no' && value?.trim()) {
        updated.police_report_not_ready = false;
      }
      return updated;
    });
  };

  const handleDocumentToggle = (field) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    const missing = validateBeforeSave(formData);
    if (missing.length > 0) {
      toast.error(`Required: ${missing.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        police_involved:
          formData.police_involved === 'Yes' ? true : formData.police_involved === 'No' ? false : null,
        date_of_loss: formData.date_of_loss ? new Date(formData.date_of_loss).toISOString() : null,
      };
      const res = await axios.put(`${API}/claims/${encodeURIComponent(claim.report_no)}`, payload);
      toast.success('Claim updated successfully');

      let freshClaim = res.data?.claim;
      if (freshClaim && res.data?.old_report_no) {
        freshClaim = { ...freshClaim, old_report_no: res.data.old_report_no };
      }
      if (!freshClaim) {
        try {
          const newRN = res.data?.report_no || claim.report_no;
          const fallback = await axios.get(`${API}/claims/${encodeURIComponent(newRN)}`);
          freshClaim = { ...fallback.data, old_report_no: claim.report_no };
        } catch { /* fallback failed */ }
      }
      onSaved?.(freshClaim || undefined);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update claim';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/claims/${encodeURIComponent(claim.report_no)}`);
      toast.success('Claim deleted');
      setDeleteConfirm(false);
      onSaved?.();
      onClose();
    } catch {
      toast.error('Failed to delete claim');
    }
  };

  const handleClose = () => {
    if (JSON.stringify(formData) !== JSON.stringify(originalData)) {
      setShowCloseWarning(true);
      return;
    }
    onClose();
  };

  const confirmClose = () => {
    setShowCloseWarning(false);
    onClose();
  };

  if (!claim) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={() => {}}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto p-0 bg-gray-100 [&>button]:hidden"
          data-testid="edit-entry-panel"
          hideClose
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <SheetDescription className="sr-only">Edit claim</SheetDescription>

          {/* Sticky close button */}
          <div className="sticky top-0 z-20 flex justify-end pointer-events-none bg-gray-100">
            <div className="pointer-events-auto relative">
              <button
                onClick={handleClose}
                className="m-2 p-1.5 text-blue-500 hover:text-blue-700 transition-colors rounded-full hover:bg-blue-50"
                data-testid="edit-entry-close"
              >
                <ChevronsRight className="w-7 h-7" />
              </button>
              {showCloseWarning && (
                <div className="absolute right-0 top-12 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-30" data-testid="close-warning-dialog">
                  <p className="text-sm text-gray-700 mb-3">You have unsaved changes.</p>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setShowCloseWarning(false)} className="text-xs h-7">Cancel</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowCloseWarning(false); handleSave(); }}
                      className="text-xs h-7 text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Save className="w-3.5 h-3.5 mr-1" />Save
                    </Button>
                    <Button size="sm" variant="destructive" onClick={confirmClose} className="text-xs h-7">Discard</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Header */}
          <div className="px-5 pt-0 pb-3 flex items-center justify-between bg-gray-100">
            <SheetTitle className="text-2xl font-extrabold text-gray-900" data-testid="edit-entry-title">
              Edit
            </SheetTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDeleteConfirm(true)}
                className="px-4 py-1.5 border border-gray-300 rounded text-sm font-semibold text-gray-600 hover:bg-gray-200 hover:text-red-600 transition-colors bg-white"
                data-testid="edit-entry-delete-btn"
              >
                Delete
              </button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-1.5 border border-gray-300 rounded text-sm font-semibold text-gray-600 hover:bg-gray-200 bg-white shadow-none"
                variant="outline"
                data-testid="edit-entry-save-btn"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {/* Form */}
          <div className="px-5 pb-6 space-y-1">
            <EntryFormFields formData={formData} handleChange={handleChange} companies={companies} />
            <EntryPicturesPanel
              claim={claim}
              pictures={pictures}
              setPictures={setPictures}
              picIndex={picIndex}
              setPicIndex={setPicIndex}
              uploading={uploading}
              setUploading={setUploading}
            />
            <EntryDocChecklist formData={formData} onToggle={handleDocumentToggle} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent className="sm:max-w-[400px] bg-white" hideClose>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600">Delete Claim</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Are you sure you want to delete <span className="font-semibold">{claim?.report_no}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(false)} className="border-gray-200">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-claim-confirm-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditEntryPanel;
